import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import Marquee from "../components/Marquee";
import ImageBlock from "../components/ImageBlock";
import { MaskedLine, Reveal } from "../components/Reveal";
import { DRINK_LIST, FOOD_LIST, TACOVERSE_URL, USE_CASES } from "../data/brand";
import { SPORTS_CATEGORIES } from "../data/sports";
import { MEDIA } from "../data/media";

function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.25]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden" data-testid="home-hero">
      <motion.div style={reduce ? {} : { y, scale: 1.14 }} className="absolute inset-0" aria-hidden="true">
        <img
          src={MEDIA.hero.src}
          alt=""
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover opacity-70"
        />
      </motion.div>
      <div
        className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/55 to-black/25"
        aria-hidden="true"
      />
      <div
        className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent"
        aria-hidden="true"
      />

      <motion.div style={reduce ? {} : { opacity: contentOpacity }} className="container-x relative pb-20 pt-36 md:pb-28">
        <p className="eyebrow !text-white/75">A modern bar experience</p>
        <h1 className="mt-5 font-display text-[clamp(3.8rem,13vw,11.5rem)] uppercase leading-[0.88] text-white">
          <MaskedLine delay={0.1}>Enter the</MaskedLine>
          <MaskedLine delay={0.24} className="glow-flame">
            Barverse
            <span className="ml-2 align-super font-mono text-[0.18em] font-bold text-white/70">™</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.42}>
          <p className="mt-6 text-xl text-white/85 md:text-2xl">Cocktails. Tacoverse. Sports. Good Times.</p>
        </MaskedLine>

        <div className="mt-9 flex flex-wrap items-center gap-4">
          <Link to="/drinks" className="btn-primary" data-testid="hero-cta-drinks">View Drinks</Link>
          <Link to="/locations" className="btn-ghost" data-testid="hero-cta-find">Find a Barverse</Link>
        </div>

        <p
          className="mt-10 flex items-center gap-3 font-mono text-[11px] font-bold uppercase tracking-[0.28em] text-white/80"
          data-testid="hero-endorsement"
        >
          <span className="inline-block h-px w-10 bg-tcv-yellow" aria-hidden="true" />
          <img src="/images/tacosun.png" alt="" className="h-5 w-auto" aria-hidden="true" />
          <span>Food by</span>
          <a href={TACOVERSE_URL} className="text-tcv-yellow transition-colors hover:text-white">
            Tacoverse™
          </a>
        </p>
      </motion.div>
    </section>
  );
}

function Welcome() {
  return (
    <section className="py-24 md:py-32" data-testid="home-welcome">
      <div className="container-x">
        <Reveal className="max-w-2xl">
          <p className="eyebrow">Barverse × Tacoverse</p>
          <h2 className="mt-5 font-display text-4xl uppercase leading-[0.95] text-bv-ink sm:text-5xl lg:text-6xl">
            Welcome to the other side.
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-bv-sub md:text-xl">
            A full bar. Your Tacoverse favorites. The game on the big screen.
          </p>
          <p className="mt-4 leading-relaxed text-bv-sub">
            Barverse is the cocktail and social experience inside select Tacoverse locations —
            same building, a different verse. Meet friends for cocktails, order Tacoverse
            favorites and catch the game on two big screens.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="mt-12">
          <ImageBlock src={MEDIA.welcome.src} alt={MEDIA.welcome.alt} className="aspect-[16/9] w-full md:aspect-[21/9]" />
        </Reveal>
      </div>
    </section>
  );
}

function GameOn() {
  return (
    <section className="border-t border-bv-line bg-bv-panel py-24 md:py-32" data-testid="home-sports">
      <div className="container-x grid items-center gap-10 lg:grid-cols-12 lg:gap-14">
        <Reveal className="lg:col-span-7">
          <ImageBlock src={MEDIA.sports.src} alt={MEDIA.sports.alt} className="aspect-[16/10] w-full" />
        </Reveal>
        <div className="lg:col-span-5">
          <Reveal delay={0.08}>
            <p className="eyebrow">Catch the game at Barverse</p>
            <h2 className="mt-5 font-display text-5xl uppercase leading-[0.95] text-bv-ink md:text-6xl">
              Game on.
            </h2>
            <p className="mt-5 font-mono text-xs font-bold uppercase tracking-[0.22em] text-bv-flame">
              Drinks. Tacos. Game on.
            </p>
            <p className="mt-4 leading-relaxed text-bv-sub">
              Two big screens keep you connected to the action — grab a drink, order your Tacoverse
              favorites and catch the game.
            </p>
            <ul className="mt-7 flex flex-wrap gap-2.5" aria-label="Sports you can watch">
              {SPORTS_CATEGORIES.map((s) => (
                <li key={s.abbr} className="chip">{s.name}</li>
              ))}
            </ul>
            <p className="mt-5 text-xs leading-relaxed text-bv-faint">
              Programming varies by location — not every game is guaranteed.
            </p>
            <Link to="/sports" className="btn-primary mt-8" data-testid="home-sports-cta">
              Sports at Barverse
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function DrinksFoodSplit() {
  return (
    <section className="py-24 md:py-32" data-testid="home-drinks-food">
      <div className="container-x grid gap-6 md:grid-cols-2">
        <Reveal>
          <div className="flex h-full flex-col rounded-2xl border border-bv-line bg-bv-panel p-8 md:p-10">
            <p className="eyebrow">The bar</p>
            <h2 className="mt-4 font-display text-4xl uppercase text-bv-ink md:text-5xl">Drinks</h2>
            <ul className="mt-8 flex-1">
              {DRINK_LIST.map((d, i) => (
                <li key={d} className="flex items-baseline gap-4 border-b border-bv-line/70 py-3 last:border-0">
                  <span className="font-mono text-[10px] font-bold text-bv-faint">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-semibold text-bv-ink transition-transform duration-200 hover:translate-x-1">{d}</span>
                </li>
              ))}
            </ul>
            <Link to="/drinks" className="btn-ghost mt-8 self-start" data-testid="home-drinks-cta">
              Explore Drinks
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="flex h-full flex-col rounded-2xl border border-bv-gold/40 bg-[#FFF6E8] p-8 md:p-10">
            <p className="eyebrow">
              <span className="flex items-center gap-2 text-[#B45309]">
                <img src="/images/tacosun.png" alt="" className="h-3.5 w-auto" aria-hidden="true" />
                Food by Tacoverse™
              </span>
            </p>
            <h2 className="mt-4 font-display text-4xl uppercase text-bv-ink md:text-5xl">Food</h2>
            <ul className="mt-8 flex-1">
              {FOOD_LIST.map((f, i) => (
                <li key={f} className="flex items-baseline gap-4 border-b border-bv-line/70 py-3 last:border-0">
                  <span className="font-mono text-[10px] font-bold text-[#D97706]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-semibold text-bv-ink transition-transform duration-200 hover:translate-x-1">{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/food" className="btn-ghost mt-8 self-start !border-tcv-yellow/40" data-testid="home-food-cta">
              Explore Food
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function TwoVerses() {
  return (
    <section className="border-y border-black/10 bg-[#1E1E1E] py-24 md:py-32" data-testid="home-two-verses">
      <div className="container-x">
        <Reveal className="text-center">
          <p className="eyebrow justify-center !text-white/70">The relationship</p>
          <h2 className="mt-5 font-display text-4xl uppercase leading-[0.95] text-white sm:text-5xl lg:text-6xl">
            Two verses. One experience.
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          <Reveal>
            <div
              className="h-full rounded-2xl border border-bv-flame/60 bg-bv-flame/15 p-8 shadow-[0_0_60px_rgba(234,91,38,0.25)] md:p-12"
              data-testid="two-verses-barverse"
            >
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-bv-flame">The bar verse</p>
              <h3 className="mt-4 font-display text-5xl uppercase text-white md:text-6xl">Barverse</h3>
              <p className="mt-4 font-mono text-xs font-bold uppercase tracking-[0.22em] text-white/70">Bar + Sports</p>
              <p className="mt-6 leading-relaxed text-white/70">
                Cocktails, tequila, draft beer and big screens — a darker, modern room built around
                the game and the crew.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div
              className="h-full rounded-2xl border border-bv-gold/50 bg-bv-gold/10 p-8 md:p-12"
              data-testid="two-verses-tacoverse"
            >
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-bv-gold">The food verse</p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <img src="/images/tacosun.png" alt="" className="h-8 w-auto md:h-10" aria-hidden="true" />
                <img src="/images/tacoverse-logo.png" alt="Tacoverse™" className="h-7 w-auto md:h-9" />
              </div>
              <p className="mt-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-bv-gold">Tex Mex Auténtico</p>
              <p className="mt-4 font-mono text-xs font-bold uppercase tracking-[0.22em] text-white/70">Food + Zero Proof</p>
              <p className="mt-6 leading-relaxed text-white/70">
                Premium Tex-Mex, fast casual and zero alcohol — the main restaurant brand serving
                its full menu inside Barverse.
              </p>
              <a
                href={TACOVERSE_URL}
                className="mt-6 inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-tcv-yellow transition-colors hover:text-white"
                data-testid="two-verses-tacoverse-link"
              >
                tacoverse.com <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Groups() {
  return (
    <section className="py-24 md:py-32" data-testid="home-groups">
      <div className="container-x grid items-center gap-10 lg:grid-cols-12 lg:gap-14">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="eyebrow">Groups</p>
            <h2 className="mt-5 font-display text-4xl uppercase leading-[0.95] text-bv-ink sm:text-5xl lg:text-6xl">
              Bring your crew.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-bv-sub">
              Game days, watch parties, birthdays, after work — if the group is loud, Barverse is
              the room.
            </p>
            <Link to="/groups" className="btn-primary mt-8" data-testid="home-groups-cta">
              Plan a Group Event
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <Reveal delay={0.08}>
            <ul className="grid grid-cols-2 gap-3 content-start sm:grid-cols-3">
              {USE_CASES.map((u, i) => (
                <li
                  key={u}
                  className="rounded-lg border border-bv-line bg-bv-panel px-4 py-3 text-center font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-bv-sub"
                >
                  <span className="mr-2 text-bv-faint">{String(i + 1).padStart(2, "0")}</span>
                  {u}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function LocationsPreview() {
  return (
    <section className="border-t border-bv-line bg-bv-panel py-24 md:py-32" data-testid="home-locations-preview">
      <div className="container-x">
        <Reveal>
          <p className="eyebrow">Locations</p>
          <h2 className="mt-5 font-display text-4xl uppercase leading-[0.95] text-bv-ink sm:text-5xl lg:text-6xl">
            Find your Barverse.
          </h2>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-bv-sub">
            Opening soon in the DFW area and North Florida — inside select Tacoverse restaurants.
          </p>
        </Reveal>
        <Reveal delay={0.08}>
          <ul className="mt-12 divide-y divide-bv-line/70 border-y border-bv-line/70">
            {["Dallas–Fort Worth area, Texas", "North Florida"].map((r) => (
              <li key={r} className="flex flex-wrap items-center justify-between gap-4 py-6">
                <span className="font-display text-2xl uppercase text-bv-ink md:text-4xl">{r}</span>
                <span className="chip !border-bv-flame/60 !text-bv-flame">Opening Soon</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal className="mt-10">
          <Link to="/locations" className="btn-ghost" data-testid="home-locations-cta">
            View Locations
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        description="Barverse is a modern cocktail and social bar — signature cocktails, drinks and Tacoverse food, with the game on two big screens. Find a Barverse and bring your crew."
        ogImage={`https://barverse.com${MEDIA.hero.src}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Barverse",
            url: "https://barverse.com",
            logo: "https://barverse.com/barverse-logo.png",
            description:
              "Modern cocktail and social bar — signature cocktails, drinks and Tacoverse food, with the game on two big screens.",
            slogan: "Cocktails. Tacoverse. Sports. Good Times.",
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Barverse",
            url: "https://barverse.com",
          },
        ]}
      />
      <Hero />
      <Marquee />
      <Welcome />
      <GameOn />
      <DrinksFoodSplit />
      <TwoVerses />
      <Groups />
      <LocationsPreview />
    </>
  );
}
